import { Box, Drawer, IconButton, Tooltip, Typography, useMediaQuery } from "@mui/material";
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import schoolSidebarConfig from "./SchoolSidebarConfig";
import { useContentSetting } from "../../Hooks/ContentSetting";
import { useGodMode } from "../../Hooks/GodModeContext";
import { useSchool } from "../../Hooks/SchoolContext";
import LogoutIcon from "@mui/icons-material/Logout";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import PersonIcon from "@mui/icons-material/PersonOutline";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { logo } from "../../assets";
import { accent, accentHover, font } from "./schoolPortalTheme";

// Sizing comes from the consumer app's Learning Hub rail (web-frontend
// `pages/learning-hub/[[...params]].jsx` — `styles.sidebar` and friends), the
// same source `../dashboard/AppSidebar.js` uses. This rail used to run its own
// heavier metrics — a 76px collapsed rail, 22px header padding, full-pill rows
// with a second line of description text under every title — which read as
// noticeably thicker than the website it is meant to match.
const EXPANDED_WIDTH = 260;   // 230 in the 600–640px band, matching the hub
const COLLAPSED_WIDTH = 44;   // 40 in that same band
const NARROW_EXPANDED_WIDTH = 230;
const NARROW_COLLAPSED_WIDTH = 40;

// The sidebar is a solid brand-red rail (the logo asset is white, so it needs
// a dark/colored background to read at all) — its own light-on-red palette,
// separate from the light-surface tokens the rest of the portal uses.
const sidebarBg = `linear-gradient(180deg, ${accent} 0%, ${accentHover} 100%)`;
const onRedBorder = "rgba(255,255,255,0.10)";
const onRedText = "rgba(255,255,255,0.55)";
const onRedTextMuted = "rgba(255,255,255,0.35)";
const onRedTile = "rgba(255,255,255,0.06)";

export default function SchoolSidebar({ handleDrawerToggle, mobileOpen }) {
  const { userInfo, setUserInfo } = useContentSetting();
  const { isGodMode, impersonatedSchool, exitGodMode } = useGodMode();
  const { school: activeSchool, modules: activeModules, isIndependentInstructor } = useSchool();
  const navigate  = useNavigate();
  const location  = useLocation();
  // Starts collapsed, like the hub rail — it is a peek-and-collapse rail
  // (clicking outside already re-collapses it), not a permanent 260px panel.
  const [collapsed, setCollapsed] = useState(true);
  const sidebarRef = useRef(null);

  // The permanent rail only exists from `sm` (600px) up, so this is the hub's
  // 640px step applied to the narrow end of that range.
  const narrow = useMediaQuery("(max-width:640px)");
  const expandedWidth  = narrow ? NARROW_EXPANDED_WIDTH  : EXPANDED_WIDTH;
  const collapsedWidth = narrow ? NARROW_COLLAPSED_WIDTH : COLLAPSED_WIDTH;

  const { product: productItems, manage: manageItems } = schoolSidebarConfig(activeModules, userInfo?.role, isIndependentInstructor);

  const MODULE_LABELS = [
    { key: "learn", label: "Learn" },
    { key: "insights", label: "Insights" },
    { key: "tutors", label: "Tutors" },
    { key: "adaptPlus", label: "Adapt+" },
  ];
  // An independent instructor's "school" is themselves, so the brand slot
  // says what the product is rather than repeating the auto-generated group
  // name (that stays in the profile area at the bottom of the rail).
  const enrolledModules = isIndependentInstructor
    ? activeSchool?.name || ""
    : MODULE_LABELS.filter((m) => activeModules[m.key]).map((m) => m.label).join(" + ");
  const brandLabel = isIndependentInstructor ? "XAir Instructor" : activeSchool?.name;

  useEffect(() => {
    function handleClickOutside(event) {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target) && !collapsed) {
        setCollapsed(true);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [collapsed]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user_data");
    localStorage.removeItem("impersonating_school");
    setUserInfo(null);
    navigate("/login");
  };

  const getUserName = () => {
    if (userInfo?.first_name && userInfo?.last_name) return `${userInfo.first_name} ${userInfo.last_name}`;
    if (userInfo?.firstName && userInfo?.lastName)   return `${userInfo.firstName} ${userInfo.lastName}`;
    return userInfo?.name || userInfo?.email || "School Admin";
  };

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  // One row = a fixed icon tile plus a 13px label, exactly as the hub draws it.
  // The per-item `description` from SchoolSidebarConfig moves into the tooltip:
  // the hub's rows are a single line, and a second line of 11px copy under
  // every title added ~18px to each of them.
  const renderNavItem = (item, expanded, isMobile) => {
    const active = isActive(item.path);
    return (
      <Tooltip
        key={item.id}
        title={expanded ? item.description || "" : item.title}
        placement="right"
        enterDelay={400}
      >
        <div
          onClick={() => { navigate(item.path); if (isMobile) handleDrawerToggle(); }}
          style={{
            padding: expanded ? "10px 14px" : "6px 0",
            margin: expanded ? "0 8px" : "0 4px",
            borderRadius: 12,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            justifyContent: expanded ? "flex-start" : "center",
            background: active ? "rgba(0,0,0,0.16)" : "transparent",
            transition: "all 0.15s ease",
            userSelect: "none",
            whiteSpace: "nowrap",
          }}
          onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
          onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
        >
          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
            width:  expanded ? 34 : 26,
            height: expanded ? 34 : 26,
            borderRadius: expanded ? "10px" : "8px",
            background: active ? "#fff" : onRedTile,
            color: active ? accent : "rgba(255,255,255,0.5)",
            transition: "all 0.15s ease",
            "& svg": { fontSize: expanded ? "18px" : "14px", color: "inherit" },
          }}>
            {item.icon}
          </Box>

          {expanded && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{
                fontSize: "13px",
                fontWeight: active ? 700 : 500,
                color: active ? "#fff" : onRedText,
                fontFamily: font,
                letterSpacing: "0.01em",
                lineHeight: 1.3,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}>
                {item.title}
              </Typography>
            </div>
          )}
        </div>
      </Tooltip>
    );
  };

  const currentWidth = collapsed ? collapsedWidth : expandedWidth;

  // Rendered once for the desktop permanent rail (respects `collapsed`) and once
  // for the mobile temporary overlay (always full-width/expanded — a mobile
  // drawer should never render the icon-only collapsed layout, since both
  // Drawer variants used to share the same inline `width` + `collapsed` state,
  // which is what caused the mobile drawer to render at the desktop's current
  // collapsed width instead of its own full-width overlay).
  const renderDrawer = (isMobile) => {
    const expanded = isMobile ? true : !collapsed;
    const width = isMobile ? EXPANDED_WIDTH : currentWidth;

    return (
      <div
        ref={isMobile ? null : sidebarRef}
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          background: sidebarBg,
          width,
          transition: isMobile ? undefined : "width 0.25s cubic-bezier(0.4,0,0.2,1)",
          overflow: "hidden",
        }}
      >
        {/* Header / Logo — always visible, even collapsed */}
        <div style={{
          // Hub metrics are 12px/10px; the collapsed rail keeps the vertical
          // 12px but drops to 4px horizontally, because the wordmark is
          // 1.95:1 and 10px of padding clips it inside a 44px rail.
          padding: expanded ? "12px 10px" : "12px 4px",
          borderBottom: `1px solid ${onRedBorder}`,
          display: "flex",
          flexDirection: "column",
          gap: expanded ? "8px" : 0,
          minHeight: "44px",
          position: "relative",
          overflow: "hidden",
        }}>
          <div style={{
            position: "absolute", top: -40, left: -30, width: 140, height: 140, borderRadius: "50%",
            background: "radial-gradient(circle, rgba(255,255,255,0.12), transparent 70%)", pointerEvents: "none",
          }} />
          <div style={{
            display: "flex",
            justifyContent: expanded ? "space-between" : "center",
            alignItems: "center",
            gap: "10px",
            position: "relative",
          }}>
            <img
              src={logo}
              alt="XAir"
              style={{
                height: expanded ? 36 : 16,
                width: "auto",
                objectFit: "contain",
                flexShrink: 0,
                filter: "drop-shadow(0 1px 4px rgba(0,0,0,0.2))",
              }}
            />
            {expanded && !isMobile && (
              <IconButton
                onClick={() => setCollapsed(true)}
                aria-label="Collapse sidebar"
                sx={{
                  background: "rgba(255,255,255,0.10)",
                  border: "none",
                  borderRadius: "8px",
                  padding: "7px",
                  color: "rgba(255,255,255,0.7)",
                  flexShrink: 0,
                  transition: "all 0.15s ease",
                  "&:hover": { background: "rgba(255,255,255,0.20)", color: "#fff" },
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </div>

          {/* Org name + enrolled modules — the hub's brandName/brandSubtitle pair */}
          {expanded && brandLabel && (
            <div style={{ position: "relative" }}>
              <Typography sx={{
                fontSize: "13px", fontWeight: 700, color: "#fff", fontFamily: font,
                lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {brandLabel}
              </Typography>
              {enrolledModules && (
                <Typography sx={{
                  fontSize: "10px", fontWeight: 500, color: onRedTextMuted, fontFamily: font,
                  lineHeight: 1.3, mt: "2px",
                }}>
                  {enrolledModules}
                </Typography>
              )}
            </div>
          )}
        </div>

        {/* Navigation */}
        <div style={{
          flex: 1,
          padding: "4px 0",
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}>
          {productItems.map((item) => renderNavItem(item, expanded, isMobile))}

          {manageItems.length > 0 && (
            <>
              {expanded ? (
                <div style={{
                  fontSize: 10, fontWeight: 700, color: onRedTextMuted, fontFamily: font,
                  letterSpacing: "0.1em", textTransform: "uppercase",
                  padding: "16px 18px 6px", marginTop: 6,
                  borderTop: `1px solid ${onRedBorder}`,
                }}>
                  Manage
                </div>
              ) : (
                <div style={{ height: 1, background: onRedBorder, margin: "6px 10px" }} />
              )}
              {manageItems.map((item) => renderNavItem(item, expanded, isMobile))}
            </>
          )}
        </div>

        {/* Expand button, when collapsed */}
        {!expanded && (
          <div style={{ padding: "4px 0", display: "flex", justifyContent: "center" }}>
            <IconButton
              onClick={() => setCollapsed(false)}
              aria-label="Expand sidebar"
              sx={{
                background: "rgba(255,255,255,0.08)",
                border: "none",
                borderRadius: "6px",
                padding: "5px",
                color: "rgba(255,255,255,0.5)",
                transition: "all 0.15s ease",
                "&:hover": { background: "rgba(255,255,255,0.18)", color: "#fff" },
              }}
            >
              <ChevronRightIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </div>
        )}

        {/* User Section */}
        <div style={{
          padding: expanded ? "14px" : "14px 0",
          borderTop: `1px solid ${onRedBorder}`,
          marginTop: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: expanded ? "stretch" : "center",
          gap: "8px",
        }}>
          {/* God Mode school badge */}
          {expanded && isGodMode && impersonatedSchool && (
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "6px 8px",
              backgroundColor: "rgba(255,255,255,0.12)",
              borderRadius: "8px",
            }}>
              <AdminPanelSettingsIcon sx={{ color: "#fff", fontSize: 14, flexShrink: 0 }} />
              <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, fontFamily: font }}>
                {impersonatedSchool.name}
              </Typography>
            </div>
          )}

          {expanded ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 8, background: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <PersonIcon sx={{ fontSize: 16, color: accent }} />
              </div>
              <Typography sx={{
                fontSize: "13px", fontWeight: 600, color: "#fff", fontFamily: font,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                flex: 1, minWidth: 0,
              }}>
                {isGodMode ? "Super Admin" : getUserName()}
              </Typography>
            </div>
          ) : (
            <div style={{
              width: 22, height: 22, borderRadius: 6, background: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <PersonIcon sx={{ fontSize: 13, color: accent }} />
            </div>
          )}

          <IconButton
            onClick={isGodMode ? () => exitGodMode(navigate) : handleLogout}
            disableRipple
            title={isGodMode ? "Exit God Mode" : "Sign out"}
            aria-label={isGodMode ? "Exit God Mode" : "Sign out"}
            sx={{
              background: "rgba(255,255,255,0.10)",
              border: "none",
              borderRadius: "8px",
              padding: "7px",
              color: "rgba(255,255,255,0.7)",
              width: expanded ? "100%" : "28px",
              height: "30px",
              transition: "all 0.15s ease",
              "&:hover": { background: "rgba(255,255,255,0.20)", color: "#fff" },
            }}
          >
            {isGodMode ? <ExitToAppIcon sx={{ fontSize: expanded ? 16 : 13 }} /> : <LogoutIcon sx={{ fontSize: expanded ? 16 : 13 }} />}
          </IconButton>
        </div>
      </div>
    );
  };

  return (
    <Box
      component="nav"
      sx={{ width: { sm: currentWidth }, flexShrink: { sm: 0 }, transition: "width 0.25s cubic-bezier(0.4,0,0.2,1)" }}
      aria-label="school portal navigation"
      className="app-sidebar-box"
    >
      <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": { width: EXPANDED_WIDTH, background: sidebarBg, border: "none" },
        }}>
        {renderDrawer(true)}
      </Drawer>

      <Drawer variant="permanent"
        sx={{
          display: { xs: "none", sm: "block" },
          "& .MuiDrawer-paper": {
            width: currentWidth,
            background: sidebarBg,
            border: "none",
            boxShadow: "4px 0 24px rgba(0,0,0,0.25)",
            transition: "width 0.25s cubic-bezier(0.4,0,0.2,1)",
            overflowX: "hidden",
          },
        }}
        open>
        {renderDrawer(false)}
      </Drawer>
    </Box>
  );
}
